/*
 * Copyright (c) 2017 CalAmp Corp.  All Rights Reserved
 */
import {saveAs} from 'file-saver';
import moment from 'moment';
import XLSX from 'xlsx/xlsx';
import {translate} from '@ion/core/src/locale/locale';
import {JobDetails} from '../types/editors';
import {ResolvedTemplateOptions} from '../types/templates';
import {formatHeaders, s2ab, toSheets} from './ExcelHelpers';
import {formatTabData} from './TemplateHelpers';

// defines the prop definitions for exporter ...
export interface ExporterType {
  title: string;
  fileName: string;
  headers: Array<string>;
  data: unknown[];
  options: ResolvedTemplateOptions;
};

/**
 * The exporter module provides a utility for exporting an XLSX or equivalent
 * related formatted Spreadsheet for download to the User's  machine based on
 * the provided arguments.
 *
 * ```typescript
 *
 * import {exporter} from '@ion/core/src/xlsx';
 *
 * const downloader = exporter('Assets', 'admin-assets',
 *   ['Name', 'Status', 'Type', 'Make', 'Model'],
 *   {appendDate: true, titles: true}
 * );
 * downloader();
 *
 * Likewise, we can initiate the download immediately by providing the
 * immediate option
 *
 * exporter('Assets', 'admin-assets',
 *   ['Name', 'Status', 'Type', 'Make', 'Model'],
 *   {appendDate: true, titles: true, immediate: true}
 *  );
 * ```
 *
 * @param  title The title of the spreadsheet.
 * @param  filename The name of the file to be downloaded.
 * @param  headers An array of column headers. To automatically
 *         convert the column headers to title strings, set options.titles
 *         to true.
 * @param  data An array of arrays containing the data for each
 *         column header (optional).
 * @param  options Additional configuration options, which are as
 *         follows:
 * @param  options.immediate If true, invokes the file download
 *         immediately.
 * @param  options.format The Spreadsheet format / file extension
 *         of the downloaded file; valid values are xls and xlsx.
 * @param  options.formatter A formatter function which processes
 *         header names to a specific strings to be displayed. If this argument
 *         is omitted, the toTitle function will be used, which will simply
 *         convert camelCase names to title formatted strings; e.g. "propA",
 *         will be converted to "Prop A".
 * @param  options.titles If true, converts header names to titles.
 * @return returns a function which executes the file
 *         download. If options.immediate is true, the download is invoked
 *         immediately, and a boolean is returned.
 */
export const exporter = ({
  title,
  fileName,
  headers,
  data,
  options = {}
}: ExporterType) => {
  const formattedHeaders = headers?.map(header => {
    if (header?.includes('__group__')) {
      return header.replace('__group__', options?.groupName ? `(${options.groupName})` : '');
    } else {
      return header;
    }
  });
  const formatter = options.formatter || formatHeaders;
  const columns   = options.titles ? [formatter(headers)] : [formattedHeaders];
  const entries   = data ? columns.concat(data) : columns;
  const wb = {
    SheetNames : [title],
    Sheets     : {
      [title] : toSheets(entries)
    }
  };
  const wbout = XLSX.write(wb, {
    bookType : options.format || 'xlsx',
    bookSST  : true,
    type     : 'binary'
  });
  const buffer = s2ab(wbout);
  const filename = options.appendDate ? `${fileName}-${new Date().toISOString()}`: fileName;
  const _exporter = () => {
    saveAs(new Blob(
      [buffer],
      {type: 'application/octet-stream'}
    ), `${filename}.${options.format || 'xlsx'}`);
  };
  if (!options.immediate) {
    return _exporter;
  }
  _exporter();
};

/**
 * The exportXLSX module provides a utility for exporting an XLSX or equivalent
 * related formatted Spreadsheet for download to the User's machine based on
 * the provided arguments in separate tabs.
 *
 * ```typescript
 *
 * import {exportXLSX} from '@ion/core/src/xlsx';
 *
 * exportXLSX({entity, jobData, taskData, deviceData
 *   jobTabColumns, taskTabColumns, deviceTabColumns
 * });
 * 
 * ```
 *
 */
export const exportXLSX = ({
  entity,
  jobData,
  taskData,
  deviceData,
  jobTabColumns,
  taskTabColumns,
  deviceTabColumns
}) => {
  const jobTab    = XLSX.utils.aoa_to_sheet(formatTabData(jobTabColumns, jobData));
  const taskTab   = XLSX.utils.aoa_to_sheet(formatTabData(taskTabColumns, taskData));
  const deviceTab = XLSX.utils.aoa_to_sheet(formatTabData(deviceTabColumns, deviceData));
  const workbook  = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, jobTab, JobDetails.JOBS);
  XLSX.utils.book_append_sheet(workbook, taskTab, JobDetails.TASKS);
  XLSX.utils.book_append_sheet(workbook, deviceTab, JobDetails.DEVICES);

  const timestamp = moment(new Date()).format('MM-DD-YYYY');
  let file        = translate(`common:export.filenames.${entity}`);
  file            = `${file}-${timestamp}`;
  file            = `${file}.xlsx`;

  XLSX.writeFile(workbook, file);
};
